import _get from 'lodash/get'
import { fromJS } from 'immutable'
import _uniqBy from 'lodash/uniqBy'
import { fromNow } from '../../services/date-time/index'
import {
  PLAYLIST_TYPE_DEFAULT,
  PLAYLIST_TYPE_FAVORITES,
  PLAYLIST_TYPE_PORTAL,
  PLAYLIST_TYPE_HISTORY,
} from '../../services/playlist/index'
import {
  URL_PLAYLIST,
  URL_PLAYLIST_FAVORITES,
  URL_PLAYLIST_WATCH_LATER,
  URL_PLAYLIST_WATCH_HISTORY,
  URL_PLAYLIST_PURCHASES_VIDEOS,
  URL_PLAYLIST_PURCHASES_EBOOKS,
  URL_PLAYLIST_BONUS_VIDEOS,
  URL_PLAYLIST_BONUS_EBOOKS,
} from '../../services/url/constants'
import { PROVIDER_GAIA_BONUS, PROVIDER_SHOPIFY } from '../../services/playlist/query'

export const PLAYLIST_FETCH_LIMIT = 16
export const PLACEHOLDER_LIST_SIZE = 4

export const pageViews = {
  PLAYLIST: 'playlist',
  WATCH_HISTORY: 'watch-history',
  PURCHASES_VIDEOS: 'purchases-videos',
  PURCHASES_EBOOKS: 'purchases-ebooks',
  BONUS_VIDEOS: 'bonus-videos',
  BONUS_EBOOKS: 'bonus-ebooks',
}

export function getPlaceholderList () {
  return fromJS(new Array(PLACEHOLDER_LIST_SIZE).fill(null).map((_, i) => ({
    titleGroupId: `${i + 1}-video`,
  })))
}

/**
 * Determines the page state based on the given pathname.
 *
 * @param {string} pathname - The current path of the application.
 * @param {Array<Object>} userPlaylists - An array of playlist objects.
 * @param {Object} userPurchases - An object of purchase objects.
 * @param {Object} staticText - An object containing static text translations.
 * @returns {Object|null} - The page state object, or null if no matching state is found.
 */
export function getPageStateByPath (pathname, userPlaylists, userPurchases, staticText) {
  if (pathname === URL_PLAYLIST_WATCH_HISTORY) {
    return {
      view: pageViews.WATCH_HISTORY,
      type: PLAYLIST_TYPE_HISTORY,
      name: staticText.get('watchHistoryTitle'),
    }
  }

  if (pathname === URL_PLAYLIST_PURCHASES_VIDEOS && userPurchases.purchases.contentList.size > 0) {
    return {
      name: staticText.get('videosTitle'),
      view: pageViews.PURCHASES_VIDEOS,
    }
  }

  if (pathname === URL_PLAYLIST_PURCHASES_EBOOKS && userPurchases.purchases.assetsList.size > 0) {
    return {
      name: staticText.get('eBooksTitle'),
      view: pageViews.PURCHASES_EBOOKS,
    }
  }

  if (pathname === URL_PLAYLIST_BONUS_VIDEOS && userPurchases.bonus.contentList.size > 0) {
    return {
      name: staticText.get('videosTitle'),
      view: pageViews.BONUS_VIDEOS,
    }
  }

  if (pathname === URL_PLAYLIST_BONUS_EBOOKS && userPurchases.bonus.assetsList.size > 0) {
    return {
      name: staticText.get('eBooksTitle'),
      view: pageViews.BONUS_EBOOKS,
    }
  }

  if (userPlaylists && userPlaylists.length > 0) {
    const playlistType = pathname.split('/').pop()
    const playlist = userPlaylists.find((item) => item.get('playlistType') === playlistType)
    if (playlist) {
      return {
        view: pageViews.PLAYLIST,
        type: playlistType,
        name: playlist.get('playlistName'),
        id: playlist.get('id'),
      }
    }
    if (pathname === URL_PLAYLIST_WATCH_LATER) {
      return {
        view: pageViews.PLAYLIST,
        type: URL_PLAYLIST_WATCH_LATER,
        name: staticText.get('watchLaterTitle'),
      }
    }
    if (pathname === URL_PLAYLIST_WATCH_LATER) {
      return {
        view: pageViews.PLAYLIST,
        type: URL_PLAYLIST_FAVORITES,
        name: staticText.get('favoritesTitle'),
      }
    }
  }
  return null
}

/**
 * @typedef {Object} Playlist
 * @property {string} playlistType - The type of the playlist.
 */

/**
 * @function getUserPlaylists
 * @description Groups playlists into default and custom categories based on their type.
 * @param {Array<Playlist>} playlists - The array of playlist objects to be processed.
 * @returns {Object} An object containing Immutable Maps: `defaultPlaylists` and `customPlaylists`.
 */
export function getUserPlaylists (playlists, staticText) {
  const defaultPlaylists = [
    {
      playlistName: staticText.get('watchLaterTitle'),
      playlistType: PLAYLIST_TYPE_DEFAULT,
      url: URL_PLAYLIST_WATCH_LATER,
    },
    {
      playlistName: staticText.get('favoritesTitle'),
      playlistType: PLAYLIST_TYPE_FAVORITES,
      url: URL_PLAYLIST_FAVORITES,
    },
  ]
  const customPlaylists = []
  playlists.forEach((playlist) => {
    switch (playlist.playlistType) {
      case PLAYLIST_TYPE_PORTAL:
        break
      case PLAYLIST_TYPE_DEFAULT:
        defaultPlaylists[0] = {
          ...playlist,
          ...defaultPlaylists[0],
        }
        break
      case PLAYLIST_TYPE_FAVORITES:
        defaultPlaylists[1] = {
          ...playlist,
          ...defaultPlaylists[1],
        }
        break
      default:
        customPlaylists.push({
          ...playlist,
          url: `${URL_PLAYLIST}/${playlist.playlistType}`,
        })
        break
    }
  })

  return {
    defaultPlaylists: fromJS(defaultPlaylists),
    customPlaylists: fromJS(customPlaylists),
  }
}
/**
 * Retrieves the purchases data made by a user, split by provider.
 * - bonus: provider = GAIA_BONUS (videos + ebooks)
 * - purchases: provider = SHOPIFY (videos + ebooks)
 *
 * @param {Array|Object} purchases - The array or object of ecommerceSkuMappings to process.
 * @returns {Object} bonus and purchases, each with contentList, assetsList
 */
export function getUserPurchases (purchases) {
  const bonus = { content: [], assets: [] }
  const purchasesData = { content: [], assets: [] }
  const items = Array.isArray(purchases) ? purchases : Object.values(purchases || {})

  items.forEach((item) => {
    const provider = _get(item, ['provider'], '')
    const contentItems = _get(item, ['contentRelated'], []) || []
    const content = contentItems.length > 0 ? _uniqBy(contentItems, 'contentId') : []
    const assets = (_get(item, ['contentProviders'], []) || []).reduce((acc, prov) => {
      const refs = _get(prov, ['content', 'contentAssetReferences'], []) || []
      acc.push(...refs.map((ref) => ref.asset).filter(Boolean))
      return acc
    }, [])
    const uniqueAssets = _uniqBy(assets, 'id')

    if (provider === PROVIDER_GAIA_BONUS) {
      bonus.content.push(...content)
      bonus.assets.push(...uniqueAssets)
    } else if (provider === PROVIDER_SHOPIFY) {
      purchasesData.content.push(...content)
      purchasesData.assets.push(...uniqueAssets)
    }
  })

  const bonusContent = _uniqBy(bonus.content, 'contentId')
  const bonusAssets = _uniqBy(bonus.assets, 'id')
  const purchasesContent = _uniqBy(purchasesData.content, 'contentId')
  const purchasesAssets = _uniqBy(purchasesData.assets, 'id')

  return {
    bonus: {
      contentList: fromJS(bonusContent),
      assetsList: fromJS(bonusAssets),
    },
    purchases: {
      contentList: fromJS(purchasesContent),
      assetsList: fromJS(purchasesAssets),
    },
  }
}

/**
 * @function getExpirationText
 * @param {string} date - The expiration date in ISO format.
 * @param {Map} staticText - An object containing static text with placeholders.
 * @returns {string} - The formatted expiration text or an empty string if no date is provided.
 */
export function getExpirationText (date, staticText) {
  if (date) {
    const expireDateText = fromNow(new Date(date))
    return staticText.get('expiresIn').replace(/\$\{ time \}/g, expireDateText)
  }
  return ''
}
